import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { writeAuditLog } from '../../lib/auditLogger';
import {
  LayoutDashboard, FileText, Bell, AlertTriangle, HelpCircle, Settings, LogOut, Building2, Users, Receipt, FileClock, History, ClipboardList, CreditCard, Mail, Gift, ShieldAlert, Wrench, Activity, Coins, BookOpen, ShieldCheck, FileLock, Menu, X
} from 'lucide-react';
import { useUser } from '../../hooks/UserContext';
import { useSystemStatus } from '../../contexts/SystemStatusContext';
import SEO from '../../components/common/SEO';

// Style for navigation links
const navLinkClasses = "flex items-center px-4 py-2.5 mt-1 text-sm text-gray-700 rounded-lg hover:bg-gray-100 transition-colors";
const activeLinkClasses = "bg-blue-100 text-blue-600 font-semibold";

// Sidebar link component
const SidebarLink = ({ to, icon, children, onClick }) => (
  <NavLink
    to={to}
    end 
    onClick={onClick}
    className={({ isActive }) => `${navLinkClasses} ${isActive ? activeLinkClasses : ''}`}>
    {icon}
    <span className="ml-3">{children}</span>
  </NavLink>
);

const AdminLayout = () => {
  const { user, systemAdminRole } = useUser();
  const { maintenanceConfig } = useSystemStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isAdminRole = systemAdminRole === 'admin';
  const isOperatorOrAdmin = systemAdminRole === 'admin' || systemAdminRole === 'operator';

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      if (user) {
        await writeAuditLog({
          action: 'SYSTEM_LOGOUT',
          resourceType: 'system_admin',
          resourceId: user.uid,
          metadata: { source: 'admin_console' }
        });
      }
      await signOut(auth);
      navigate('/admin/login');
    } catch (error) {
      console.error("Admin logout error:", error);
    }
  };

  const formatMaintenanceNotice = () => {
    if (!maintenanceConfig || !maintenanceConfig.enabled || !maintenanceConfig.startAt || !maintenanceConfig.endAt) {
      return null;
    }
    const start = maintenanceConfig.startAt.toDate ? maintenanceConfig.startAt.toDate() : new Date(maintenanceConfig.startAt);
    const end = maintenanceConfig.endAt.toDate ? maintenanceConfig.endAt.toDate() : new Date(maintenanceConfig.endAt);

    const pad = (n) => n.toString().padStart(2, '0');
    const startStr = `${start.getFullYear()}/${start.getMonth() + 1}/${start.getDate()} ${start.getHours()}:${pad(start.getMinutes())}`;
    const endStr = `${end.getFullYear()}/${end.getMonth() + 1}/${end.getDate()} ${end.getHours()}:${pad(end.getMinutes())}`;

    return `以下の日時でメンテナンスを実施します ${startStr} - ${endStr}`;
  };

  const noticeText = formatMaintenanceNotice();

  const navContent = (closeMenu = () => {}) => (
    <nav className="flex-1 px-3 py-4 overflow-y-auto">
      <SidebarLink to="/admin" icon={<LayoutDashboard size={18} />} onClick={closeMenu}>ダッシュボード</SidebarLink>

      {isAdminRole && (
        <div className="mt-4">
          <h2 className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">コンテンツ管理</h2>
          <SidebarLink to="/admin/lp" icon={<FileText size={18} />} onClick={closeMenu}>LPコンテンツ</SidebarLink>
          <SidebarLink to="/admin/announcements" icon={<Bell size={18} />} onClick={closeMenu}>お知らせ</SidebarLink>
          <SidebarLink to="/admin/status" icon={<AlertTriangle size={18} />} onClick={closeMenu}>障害・メンテナンス</SidebarLink>
          <SidebarLink to="/admin/faq" icon={<HelpCircle size={18} />} onClick={closeMenu}>FAQ</SidebarLink>
          <SidebarLink to="/admin/release-notes" icon={<BookOpen size={18} />} onClick={closeMenu}>リリースノート</SidebarLink>
        </div>
      )}

      <div className="mt-4">
        <h2 className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">顧客管理</h2>
        <SidebarLink to="/admin/companies" icon={<Building2 size={18} />} onClick={closeMenu}>企業一覧</SidebarLink>
        <SidebarLink to="/admin/users" icon={<Users size={18} />} onClick={closeMenu}>ユーザー一覧</SidebarLink>
        {isAdminRole && (
          <>
            <SidebarLink to="/admin/email-magazine" icon={<Mail size={18} />} onClick={closeMenu}>メールマガジン</SidebarLink>
            <SidebarLink to="/admin/billing" icon={<CreditCard size={18} />} onClick={closeMenu}>Billwave利用料</SidebarLink>
            <SidebarLink to="/admin/bulk-refunds" icon={<Coins size={18} />} onClick={closeMenu}>一括返金管理</SidebarLink>
          </>
        )}
      </div>

      <div className="mt-4">
        <h2 className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">ログ</h2>
        <SidebarLink to="/admin/csv-history" icon={<History size={18} />} onClick={closeMenu}>CSV処理履歴</SidebarLink>
        <SidebarLink to="/admin/invoices" icon={<Receipt size={18} />} onClick={closeMenu}>請求書発行履歴</SidebarLink>
        <SidebarLink to="/admin/stripe-logs" icon={<FileClock size={18} />} onClick={closeMenu}>Stripe連携ログ</SidebarLink>
        <SidebarLink to="/admin/activity" icon={<ClipboardList size={18} />} onClick={closeMenu}>アクティビティログ</SidebarLink>
        {isAdminRole && (
          <>
            <SidebarLink to="/admin/operation-logs" icon={<ShieldAlert size={18} />} onClick={closeMenu}>操作履歴</SidebarLink>
            <SidebarLink to="/admin/audit-logs" icon={<FileLock size={18} />} onClick={closeMenu}>Audit Log</SidebarLink>
          </>
        )}
      </div>

      {isOperatorOrAdmin && (
        <div className="mt-4">
          <h2 className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">システム</h2>
          <SidebarLink to="/admin/failure-history" icon={<Activity size={18} />} onClick={closeMenu}>システム監視</SidebarLink>
          {isAdminRole && (
            <>
              <SidebarLink to="/admin/trial-settings" icon={<Gift size={18} />} onClick={closeMenu}>トライアル設定</SidebarLink>
              <SidebarLink to="/admin/stripe-settings" icon={<Settings size={18} />} onClick={closeMenu}>Stripe設定</SidebarLink>
            </>
          )}
          <SidebarLink to="/admin/maintenance-mode" icon={<Wrench size={18} />} onClick={closeMenu}>メンテナンスモード</SidebarLink>
          {isAdminRole && (
            <SidebarLink to="/admin/system-users" icon={<ShieldCheck size={18} />} onClick={closeMenu}>システムユーザー管理</SidebarLink>
          )}
        </div>
      )}
    </nav>
  );

  return (
    <>
      <SEO title="Billwave 管理画面" description="Billwave Admin Console" robots="noindex, nofollow" path="/admin" />
      <div className="flex flex-col min-h-screen bg-gray-50 font-sans">
        {/* 1. System Maintenance Banner Bar (Top Dark Blue) */}
        {noticeText && (
          <div className="bg-[#003875] text-white text-xs font-semibold py-2 px-4 text-center flex items-center justify-center gap-2 z-50">
            <span className="inline-block border border-white rounded-full w-4 h-4 text-center leading-3 font-mono text-[10px]">i</span>
            <span>{noticeText}</span>
          </div>
        )}

        {/* 2. Global Header Bar */}
        <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between flex-shrink-0 z-40 sticky top-0">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-1.5 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100 focus:outline-none"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <img src="/assets/logo.png" alt="Billwave" className="h-7 sm:h-8" />
            <span className="text-blue-600 font-bold text-base sm:text-lg whitespace-nowrap">管理画面</span>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <span className="text-xs sm:text-sm text-gray-700 font-medium truncate max-w-[120px] sm:max-w-xs">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="py-1 px-3 sm:py-1.5 sm:px-4 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors shadow-sm whitespace-nowrap"
            >
              ログアウト
            </button>
          </div>
        </header>

        {/* Main Container */}
        <div className="flex flex-1 overflow-hidden relative">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:flex w-64 bg-white border-r border-gray-200 flex-shrink-0 flex-col">
            {navContent()}
          </aside>

          {/* Mobile Backdrop */}
          {isMobileMenuOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}

          {/* Mobile Sidebar Off-canvas */}
          <aside
            className={`fixed top-0 left-0 bottom-0 w-64 bg-white z-50 flex flex-col border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:hidden ${
              isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="h-16 border-b border-gray-200 px-4 flex items-center justify-between">
              <span className="text-blue-600 font-bold text-lg">管理メニュー</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            {navContent(() => setIsMobileMenuOpen(false))}
          </aside>

          {/* Main Content */}
          <main className="flex-1 p-3 sm:p-6 lg:p-10 overflow-auto w-full">
            <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm border border-gray-100 overflow-x-auto">
                <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default AdminLayout;
